import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import CustomUserChip from '../../../../../components/CustomUserChip';
import CustomGenericTittleIcon from "../../../../../components/CustomGenericTittleIconDataChip";
import { POSITION_MODEL } from "../../../../../Helpers/PositionModel";
import TableFunction from "../../../../../components/table/TableFunction";
import { VIEW_TYPE } from "../../../../../enums/ViewType";
import {getFieldTypeById} from "../Model/Model";
import NotInformation from "../../../../../components/NotInformation";

/**
 * RenderExpandedRow displays custom fields for an expanded row within a table.
 * It handles pagination for these fields and renders each with appropriate labels and icons.
 *
 * @param {Object} props
 * @param {Object} props.data - Data object containing the fields to display.
 *
 * @returns {JSX.Element} The expanded row content with paginated custom fields.
 */
const RenderExpandedRow = ({ data }) => {
    // Local state to hold paginated field data
    const [results, setResults] = useState([]);

    // Use table utility hook to manage pagination and translations
    const { t, pagination, page, rowsPerPage } = TableFunction({
        data: { content: results, totalElements: data?.fields.length }, // Provide paginated content info
        labelRowsPerPage: 'custom_fields_per_page', // Pagination label for rows per page
        hasContainer: true, // Enable outer container for the table
        fixedPagination: false, // Allow flexible pagination
        view: VIEW_TYPE.GRID.name
    });

    /**
     * Effect to update the paginated data whenever page or rowsPerPage change.
     */
    useEffect(() => {
        getData(data?.fields);
    }, [page, rowsPerPage]);

    /**
     * Slices and updates the results based on current pagination.
     *
     * @param {Array} fields - Array of field objects to display.
     */
    const getData = (fields) => {
        setResults(fields.slice(page * rowsPerPage, (page + 1) * rowsPerPage));
    };

    return (
        <Box>
            <div className="row">
                {/* Title for the custom fields section */}
                <div className="col-12">
                    <CustomGenericTittleIcon Icon={undefined} title={t('custom_fields')} />
                </div>

                {/* Render each field as a row with chips */}
                { results?.length === 0 ?
                    <NotInformation text={t('you_haven_not_created_any_custom_field')} card={false} />
                    :
                    <>
                        {results?.map((field) => (
                            <div key={field.id} className="row mt-3">
                                {/* Field name */}
                                <div className="col-sm-12 col-md-2 col-xxl-2 col-lg-2 pl-0">
                                    {field?.fieldName}
                                </div>

                                {/* Field details: type and required/optional */}
                                <div className="col-sm-12 col-md-10 col-xxl-10 col-lg-10">
                                    <div className="col-12 p-0 d-flex flex-wrap">
                                        <CustomUserChip
                                            className="mt-0 chip-secondary-150-100"
                                            name={t(getFieldTypeById(field?.fieldType)?.name)}
                                            showAvatar={true}
                                            Icon={getFieldTypeById(field?.fieldType)?.icon}
                                            classNameIcon={"icon-color-secondary-700"}
                                            variant="compact"
                                            iconPosition={POSITION_MODEL.LEFT}
                                        />

                                        <CustomUserChip
                                            className="mt-0"
                                            name={field?.required ? t('required') : t('optional')}
                                            variant="compact"
                                        />
                                    </div>
                                </div>

                                {/* Separator line */}
                                <div className="border-bottom-shades-300 mt-3" />
                            </div>
                        ))}

                        {/* Pagination controls */}
                        <div className="col-12 mt-4">
                            {pagination()}
                        </div>
                    </>
                }
            </div>
        </Box>
    );
};

export default RenderExpandedRow;
